/* eslint-disable import/no-cycle */
import React, { useState } from 'react';
import { useTranslate, useGetList, Button } from 'react-admin';
import { Link } from 'react-router-dom';

import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Hidden from '@material-ui/core/Hidden';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import moment from 'moment';
import CustomerCommunicationsView from './CustomerCommunications';
import { getZ3PConfigValue } from '../../helper';
import AppointmentDetails from './AppointmentDetails';
import VehicleRow from './VehicleRow';
import AddressRow from './AddressRow';
import PreferencesRow from './PreferencesRow';
import PaymentRow from './PaymentRow';
import HomeAddressEdit from './HomeAddressEdit';
import BusinessAddressEdit from './BusinessAddressEdit';
import CustomerDetailsHeader from './CustomerDetailsHeader';
import FleetRow from './FleetRow';
import NoteRow from './NoteRow';

export const useCustomerDetailsStyles = makeStyles(
  (theme) => ({
    detailsContainer: {
      marginBottom: '20px',
    },
    detailsHeader: {
      padding: '45px',
      boxShadow: 'none',
      display: 'flex',
    },
    profilePhoto: {
      marginRight: '20px',
      borderRadius: '10px',
      fontSize: '120px',
    },
    profilePhotoMini: {
      fontSize: '20px',
      borderRadius: '10px',
      marginRight: '5px',
      paddingTop: '5px',
    },
    addIcon: {
      fontSize: '20px',
      borderRadius: '20px',
      marginRight: '5px',
      marginTop: '5px',
      padding: '5px',
      backgroundColor: 'rgba(0, 0, 0, 0.87)',
      color: 'white',
    },
    nowrap: {
      whiteSpace: 'nowrap',
    },
    contactIcon: {
      fontSize: '14px',
      backgroundColor: theme.palette.primary.main,
      border: `1px solid ${theme.palette.primary.main}`,
      borderRadius: '50%',
      color: 'white',
      padding: '5px',
      marginRight: '5px',
    },
    sectionCol: {
      margin: '0 20px',
    },
    sectionHeader: {
      margin: '30px 0 20px 0',
    },
    appointmentStatus: {
      margin: '10px 0',
    },
    paperBase: {
      position: 'relative',
      padding: '20px',
      boxShadow: '0px 2px 10px rgba(117, 117, 117, 0.25)',
      borderRadius: '10px',
    },
    kebabMenu: {
      position: 'absolute',
      top: '10px',
      right: '10px',
    },
    pointer: {
      cursor: 'pointer',
    },
    gray: {
      color: '#888',
    },
    vehicleImage: {
      minWidth: '85px',
      textAlign: 'center',
    },
    cardIcon: {
      display: 'flex',
      minWidth: '35px',
      justifyContent: 'center',
    },
    cardTextContent: {
      margin: '0 8px',
    },
    fw500: {
      fontWeight: 500,
    },
    w100: {
      width: '100%',
    },
    listIcon: {
      '& .MuiListItemIcon-root': {
        minWidth: 35,
      },
    },
  }),
  { name: 'CustomerDetails' },
);

const sortByDateAndVehicle = (a, b) => {
  if (!a.date) return -1;
  if (!b.date) return 1;
  if (a.date === b.date) {
    return a.vehicle_id > b.vehicle_id ? 1 : -1;
  }
  return a.date > b.date ? 1 : -1;
};

const swDateIsInPast = (date) => moment(date).isBefore(new Date(), 'day');

const CustomerDetails = (props) => {
  const { record } = props;
  const classes = useCustomerDetailsStyles();
  const translate = useTranslate();
  const [customer, setCustomer] = useState(record);

  const {
    default_client_location_id,
    addresses = [],
    vehicles = [],
    sws = [],
  } = record;

  // ---------------------------- Appointments
  const upcomingAppointments = sws
    .filter(
      (sw) =>
        ['scheduled', 'keys_available'].includes(sw.status) &&
        !swDateIsInPast(sw.date),
    )
    .sort((a, b) => sortByDateAndVehicle(a, b));

  const suggestedAppointments = sws
    .filter(
      (sw) => sw.status === 'suggested' && Date.parse(sw.date) >= Date.now(),
    )
    .sort((a, b) => sortByDateAndVehicle(a, b));

  const pastAppointments = sws
    .filter((sw) => sw.status === 'finished' || swDateIsInPast(sw.date))
    .sort((a, b) => sortByDateAndVehicle(a, b));

  const cancelledAppointments = sws
    .filter((sw) => sw.status === 'cancelled')
    .sort((a, b) => sortByDateAndVehicle(a, b));

  const [
    numUpcomingAppointmentsToShow,
    setNumUpcomingAppointmentsToShow,
  ] = useState(Math.min(5, upcomingAppointments.length));
  const [
    numSuggestedAppointmentsToShow,
    setNumSuggestedAppointmentsToShow,
  ] = useState(Math.min(5, suggestedAppointments.length));
  const [numPastAppointmentsToShow, setNumPastAppointmentsToShow] = useState(
    Math.min(5, pastAppointments.length),
  );
  const [
    numCancelledAppointmentsToShow,
    setNumCancelledAppointmentsToShow,
  ] = useState(Math.min(1, cancelledAppointments.length));

  // ---------------------------- Fleet
  const showFleetFields = getZ3PConfigValue('enable_fleet_in_admin_portal');

  // ---------------------------- Vehicles
  const isSkippingVehicle = getZ3PConfigValue('is_skipping_vehicle');

  const [numVehiclesToShow, setNumVehiclesToShow] = useState(
    Math.min(5, vehicles.length),
  );

  const findVehicleBySw = (sw) => {
    if (sw.vehicle_id) {
      return vehicles.find((v) => v.id === sw.vehicle_id);
    }
    return null;
  };

  // ---------------------------- Carfax
  const isCarfaxEnabled = getZ3PConfigValue('is_carfax_enabled');

  // ---------------------------- Addresses
  const [openHomeAddressEdit, setOpenHomeAddressEdit] = useState(false);
  const [homeAddressId, setHomeAddressId] = useState(null);
  const [openBusinessAddressEdit, setOpenBusinessAddressEdit] = useState(false);
  const [numHomeAddressesToShow, setNumHomeAddressesToShow] = useState(
    Math.min(5, addresses.length),
  );

  const sortedAddresses = addresses.sort((a, b) => b.is_primary - a.is_primary);

  const { total: clientLocationsLength, data: clientLocations } = useGetList(
    'client-location',
    { page: 1, perPage: 1000 },
    { field: 'name', order: 'ASC' },
    { is_active: true },
  );
  const serviceLocations = getZ3PConfigValue('service_locations');
  const hasWorkplaceServices = serviceLocations?.includes('workplace');
  const hasShopServices = serviceLocations?.includes('shop');
  const hasHomeServices = serviceLocations?.includes('home');
  const clientLocation = Object.values(clientLocations)?.find(
    (cl) => cl.id === default_client_location_id,
  );
  const showDefaultClientLocationCard =
    (hasWorkplaceServices || hasShopServices) && clientLocationsLength > 1;

  const onEditHomeAddress = (id) => {
    setOpenHomeAddressEdit(true);
    setHomeAddressId(id);
  };

  const onEditClientLocation = () => {
    setOpenBusinessAddressEdit(true);
  };

  // ---------------------------- Render

  return (
    <div className={classes.detailsContainer}>
      <CustomerDetailsHeader customer={customer} setCustomer={setCustomer} />
      <Grid container justify="center">
        <Grid item xs={12} md={8} xl={7} className={classes.sectionCol}>
          <Typography className={classes.sectionHeader} variant="h6">
            {translate('resources.customer.tabs.appointments')}
          </Typography>
          {/* Always show Scheduled section even if there are no upcoming appointments to guarantee there's always something under Appointments */}
          <Typography
            className={`${classes.appointmentStatus} ${classes.fw500}`}
            gutterBottom
          >
            Scheduled
          </Typography>
          {upcomingAppointments.length ? (
            <>
              {upcomingAppointments
                .slice(0, numUpcomingAppointmentsToShow)
                .map((sw) => (
                  <Card style={{ margin: '20px 0' }} key={sw.id}>
                    <AppointmentDetails
                      customer={customer}
                      sw={sw}
                      vehicle={findVehicleBySw(sw)}
                    />
                  </Card>
                ))}
              <ShowMoreOrLess
                totalNum={upcomingAppointments.length}
                numToShow={numUpcomingAppointmentsToShow}
                setNumToShow={setNumUpcomingAppointmentsToShow}
              />
            </>
          ) : (
            <Card style={{ margin: '20px 0' }}>
              <Typography className={classes.gray}>
                No upcoming appointments
              </Typography>
            </Card>
          )}
          {/* Only show Suggested section if there are any suggested appointments */}
          {!!suggestedAppointments.length && (
            <>
              <Typography
                className={`${classes.appointmentStatus} ${classes.fw500}`}
                gutterBottom
              >
                Suggested
              </Typography>
              {suggestedAppointments
                .slice(0, numSuggestedAppointmentsToShow)
                .map((sw) => (
                  <Card style={{ margin: '20px 0' }} key={sw.id}>
                    <AppointmentDetails
                      customer={customer}
                      sw={sw}
                      vehicle={findVehicleBySw(sw)}
                    />
                  </Card>
                ))}
              <ShowMoreOrLess
                totalNum={suggestedAppointments.length}
                numToShow={numSuggestedAppointmentsToShow}
                setNumToShow={setNumSuggestedAppointmentsToShow}
              />
            </>
          )}
          {/* Only show Past section if there are any past appointments */}
          {!!pastAppointments.length && (
            <>
              <Typography
                className={`${classes.appointmentStatus} ${classes.fw500}`}
                gutterBottom
              >
                Past
              </Typography>
              {pastAppointments
                .slice(0, numPastAppointmentsToShow)
                .map((sw) => (
                  <Card style={{ margin: '20px 0' }} key={sw.id}>
                    <AppointmentDetails
                      customer={customer}
                      sw={sw}
                      vehicle={findVehicleBySw(sw)}
                    />
                  </Card>
                ))}
              <ShowMoreOrLess
                totalNum={pastAppointments.length}
                numToShow={numPastAppointmentsToShow}
                setNumToShow={setNumPastAppointmentsToShow}
              />
            </>
          )}
          {/* Only show Cancelled section if there are any cancelled appointments */}
          {!!cancelledAppointments.length && (
            <>
              <Typography
                className={`${classes.appointmentStatus} ${classes.fw500}`}
                gutterBottom
              >
                Cancelled
              </Typography>
              {cancelledAppointments
                .slice(0, numCancelledAppointmentsToShow)
                .map((sw) => (
                  <Card style={{ margin: '20px 0' }} key={sw.id}>
                    <AppointmentDetails
                      customer={customer}
                      sw={sw}
                      vehicle={findVehicleBySw(sw)}
                    />
                  </Card>
                ))}
              <ShowMoreOrLess
                totalNum={cancelledAppointments.length}
                minNum={1}
                numToShow={numCancelledAppointmentsToShow}
                setNumToShow={setNumCancelledAppointmentsToShow}
              />
            </>
          )}
          <Typography className={classes.sectionHeader} variant="h6">
            {translate('resources.customer.tabs.communications')}
          </Typography>
          <Card style={{ margin: '20px 0' }}>
            <Grid item>
              <CustomerCommunicationsView record={customer} />
            </Grid>
          </Card>
        </Grid>
        <Hidden lgDown>
          <Divider orientation="vertical" flexItem />
        </Hidden>
        <Grid item xs={12} md={8} xl={4} className={classes.sectionCol}>
          <>
            <Typography className={classes.sectionHeader} variant="h6">
              Notes on the Customer
            </Typography>
            <Card>
              <NoteRow customer={customer} />
            </Card>
          </>
          {showFleetFields && (
            <>
              <Typography className={classes.sectionHeader} variant="h6">
                Fleet
              </Typography>
              <Card>
                <FleetRow customer={customer} />
              </Card>
            </>
          )}
          {!isSkippingVehicle && (
            <>
              <Typography className={classes.sectionHeader} variant="h6">
                {translate('resources.customer.tabs.vehicles')}
                <Button
                  label="Create Vehicle"
                  component={Link}
                  to={{
                    pathname: '/vehicle/create',
                    state: {
                      record,
                    },
                  }}
                />
              </Typography>
              <Card>
                {vehicles.length ? (
                  <>
                    {vehicles.slice(0, numVehiclesToShow).map((v) => (
                      <VehicleRow key={v.id} vehicle={v} />
                    ))}
                    <ShowMoreOrLess
                      totalNum={vehicles.length}
                      numToShow={numVehiclesToShow}
                      setNumToShow={setNumVehiclesToShow}
                    />
                  </>
                ) : (
                  <Typography className={classes.gray}>
                    No vehicles yet
                  </Typography>
                )}
              </Card>
            </>
          )}
          {hasHomeServices && (
            <>
              <Typography className={classes.sectionHeader} variant="h6">
                {translate('resources.customer.subSections.home_address')}
              </Typography>
              <Card>
                {addresses.length ? (
                  <>
                    {sortedAddresses
                      .slice(0, numHomeAddressesToShow)
                      .map((a) => (
                        <AddressRow
                          key={a.id}
                          address={a}
                          onEdit={onEditHomeAddress}
                        />
                      ))}
                    <ShowMoreOrLess
                      totalNum={addresses.length}
                      numToShow={numHomeAddressesToShow}
                      setNumToShow={setNumHomeAddressesToShow}
                    />
                  </>
                ) : (
                  <Typography className={classes.gray}>
                    No addresses yet
                  </Typography>
                )}
              </Card>
            </>
          )}
          {showDefaultClientLocationCard && (
            <>
              <Typography className={classes.sectionHeader} variant="h6">
                {translate('resources.customer.subSections.business_address')}
              </Typography>
              <Card>
                <AddressRow
                  address={clientLocation?.address}
                  onEdit={onEditClientLocation}
                  hasWorkplaceServices={hasWorkplaceServices}
                  clientLocationName={clientLocation?.long_name}
                />
              </Card>
            </>
          )}
          <Typography className={classes.sectionHeader} variant="h6">
            {translate('resources.customer.subSections.payment')}
          </Typography>
          <Card>
            <PaymentRow customer={customer} />
          </Card>

          {isCarfaxEnabled && (
            <>
              <Typography className={classes.sectionHeader} variant="h6">
                {translate('resources.customer.sections.preferences')}
              </Typography>
              <Card>
                <PreferencesRow customer={customer} />
              </Card>
            </>
          )}
        </Grid>
      </Grid>
      {/* Address Edit Modals */}
      {openHomeAddressEdit && (
        <HomeAddressEdit
          openEdit={openHomeAddressEdit}
          setOpenEdit={setOpenHomeAddressEdit}
          id={homeAddressId}
        />
      )}
      {openBusinessAddressEdit && (
        <BusinessAddressEdit
          openEdit={openBusinessAddressEdit}
          setOpenEdit={setOpenBusinessAddressEdit}
          {...props}
        />
      )}
    </div>
  );
};

export default CustomerDetails;

const Card = ({ children, style }) => {
  const classes = useCustomerDetailsStyles();
  return (
    <Paper className={classes.paperBase} style={style}>
      <Grid container direction="column" spacing={2}>
        {/* children should be Grid items */}
        {children}
      </Grid>
    </Paper>
  );
};

const ShowMoreOrLess = ({ totalNum, numToShow, setNumToShow, minNum = 5 }) => {
  const classes = useCustomerDetailsStyles();

  const handleClick = () => {
    if (numToShow < totalNum) {
      setNumToShow((prev) => Math.min(prev + 5, totalNum));
    } else {
      setNumToShow(Math.min(minNum, totalNum));
    }
  };

  if (totalNum <= 5) return null;
  return (
    <Typography
      variant="caption"
      align="center"
      className={classes.pointer}
      style={{ display: 'block' }}
      onClick={handleClick}
    >
      Show {numToShow < totalNum ? 'more' : 'less'}
    </Typography>
  );
};
